import { createFileRoute } from '@tanstack/react-router';

import type { SidePanel } from '../../../features/messenger/ui/chat-view.tsx';
import { ChatRoute } from '../../../features/messenger/ui/chat-route.tsx';

/**
 * Чат: `?message=` — перейти к сообщению с этим номером; `?panel=info|media|pins` — сведения, фото и файлы
 * или закрепы;
 * `?thread=` — открыт тред этого сообщения; `?topic=` — тема чата.
 */
interface ChatSearch {
  readonly message?: number | undefined;
  readonly panel?: 'info' | 'media' | 'pins' | undefined;
  readonly thread?: string | undefined;
  readonly topic?: string | undefined;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/messages/$conversationId')({
  validateSearch: (search: Record<string, unknown>): ChatSearch => {
    const message = Number(search.message);
    return {
      message: Number.isSafeInteger(message) && message > 0 ? message : undefined,
      panel:
        search.panel === 'info' || search.panel === 'media' || search.panel === 'pins'
          ? search.panel
          : undefined,
      thread: typeof search.thread === 'string' && UUID.test(search.thread) ? search.thread : undefined,
      topic: typeof search.topic === 'string' && UUID.test(search.topic) ? search.topic : undefined,
    };
  },
  component: ChatPage,
});

function ChatPage() {
  const { conversationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const panel: SidePanel | null = search.thread
    ? { kind: 'thread', rootId: search.thread }
    : search.panel
      ? { kind: search.panel }
      : null;
  return (
    <ChatRoute
      conversationId={conversationId}
      focusSeq={search.message ?? null}
      topicId={search.topic ?? null}
      onTopic={(topic) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            topic: topic ?? undefined,
            message: undefined,
            thread: undefined,
            panel: prev.panel === 'pins' ? undefined : prev.panel,
          }),
        });
      }}
      panel={panel}
      onPanel={(next) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            panel:
              next?.kind === 'info' || next?.kind === 'media' || next?.kind === 'pins'
                ? next.kind
                : undefined,
            thread: next?.kind === 'thread' ? next.rootId : undefined,
          }),
          replace: true,
        });
      }}
    />
  );
}
